import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import { faDownload } from '@fortawesome/free-solid-svg-icons'

import { Title, Card, Icon, Paragraph } from 'src/components'
import SelectedDates from 'src/components/SelectedDates/SelectedDates'
import { loadingSelector } from 'src/selectors/loading.selector'
import { hasErrors, handlerError } from 'src/selectors/error.selector'
import { showAlert } from 'src/actions/alert.actions'
import { isAllowed } from 'src/selectors/modules.selector'
import {
  actions,
  downloadFactura,
  downloadCreditoFiscal,
} from 'src/actions/anexos.actions'
import { anexosPermissions } from 'src/enums/permissions'

const DATES_MESSAGE_ERROR = 'El rango de fechas no puede superar 3 meses calendario.'

const Anexos = () => {
  const dispatch = useDispatch()

  const canFactura: boolean = useSelector(state =>
    isAllowed(state, [anexosPermissions.factura]),
  )
  const canCreditoFiscal: boolean = useSelector(state =>
    isAllowed(state, [anexosPermissions.creditoFiscal]),
  )

  const loadingFactura: boolean = useSelector(state =>
    loadingSelector([actions.DOWNLOAD_FACTURA])(state),
  )
  const loadingCreditoFiscal: boolean = useSelector(state =>
    loadingSelector([actions.DOWNLOAD_CREDITO_FISCAL])(state),
  )

  const errorFactura = useSelector(state => hasErrors([actions.DOWNLOAD_FACTURA])(state))
  const errorCreditoFiscal = useSelector(state =>
    hasErrors([actions.DOWNLOAD_CREDITO_FISCAL])(state),
  )

  const [dates, setDates] = useState<{ start: number; end: number }>({
    start: new Date().setHours(0, 0, 0, 0),
    end: new Date().setHours(23, 59, 59, 99),
  })

  const [flagFactura, setFlagFactura] = useState<boolean>(false)
  const [flagCreditoFiscal, setFlagCreditoFiscal] = useState<boolean>(false)

  useEffect(() => {
    if (loadingFactura) setFlagFactura(true)
    else if (flagFactura) {
      setFlagFactura(false)
      if (errorFactura) dispatch(showAlert(handlerError(errorFactura.message)))
    }
  }, [loadingFactura])

  useEffect(() => {
    if (loadingCreditoFiscal) setFlagCreditoFiscal(true)
    else if (flagCreditoFiscal) {
      setFlagCreditoFiscal(false)
      if (errorCreditoFiscal)
        dispatch(showAlert(handlerError(errorCreditoFiscal.message)))
    }
  }, [loadingCreditoFiscal])

  const isValidDateRange = (start: number, end: number): boolean => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    const startMonths = startDate.getFullYear() * 12 + startDate.getMonth()
    const endMonths = endDate.getFullYear() * 12 + endDate.getMonth()
    return endMonths - startMonths + 1 <= 3
  }

  const validDateAlert = (start: number, end: number) => {
    if (!isValidDateRange(start, end)) {
      dispatch(showAlert(handlerError(DATES_MESSAGE_ERROR)))
      return false
    }
    return true
  }

  const handleDownload = (type: 'factura' | 'creditoFiscal') => {
    if (!validDateAlert(dates.start, dates.end)) return
    const params = { start: dates.start, end: dates.end }
    if (type === 'factura') dispatch(downloadFactura(params))
    else dispatch(downloadCreditoFiscal(params))
  }

  const anyLoading = loadingFactura || loadingCreditoFiscal

  const reports: Array<{
    key: 'factura' | 'creditoFiscal'
    title: string
    allowed: boolean
    loading: boolean
  }> = [
    {
      key: 'factura',
      title: 'Factura',
      allowed: canFactura,
      loading: loadingFactura,
    },
    {
      key: 'creditoFiscal',
      title: 'Crédito Fiscal',
      allowed: canCreditoFiscal,
      loading: loadingCreditoFiscal,
    },
  ]

  return (
    <div>
      <Title title={'Anexos'} />

      <Card title={'filtros'} white>
        <Row className={'mb-4'}>
          <Col xl={12} lg={12} md={12}>
            <SelectedDates
              onDateChange={(start, end) => setDates({ start, end })}
              initialPreset={{ value: 0, label: 'Hoy' }}
            />
          </Col>
          {!isValidDateRange(dates.start, dates.end) && (
            <Col xl={12}>
              <Paragraph style={{ color: 'red' }} dim bold>
                {DATES_MESSAGE_ERROR}
              </Paragraph>
            </Col>
          )}
        </Row>
      </Card>

      <Row>
        {reports
          .filter(r => r.allowed)
          .map(r => {
            const disabled = anyLoading && !r.loading
            return (
              <Col key={r.key} xl={6} lg={6} md={6} sm={12} className={'mb-3'}>
                <div
                  className={'card'}
                  onClick={() => !anyLoading && handleDownload(r.key)}
                  style={{
                    cursor: anyLoading ? 'default' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                    userSelect: 'none',
                  }}>
                  <div
                    className={
                      'card-body d-flex flex-column align-items-center justify-content-center'
                    }
                    style={{ padding: '2rem 1.5rem', gap: 12 }}>
                    <span
                      className={'font-weight-bold'}
                      style={{ fontSize: 16, textAlign: 'center' }}>
                      {r.title}
                    </span>

                    <Icon
                      icon={faDownload}
                      spin={r.loading}
                      tooltip={'Descargar reporte'}
                      size={'4x'}
                    />
                  </div>
                </div>
              </Col>
            )
          })}
      </Row>
    </div>
  )
}

export default Anexos
